import { useState, useEffect } from "react";
import { IoIosClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import {
  FacebookShareButton,
  LineShareButton,
  EmailShareButton,
  FacebookIcon,
  LineIcon,
  EmailIcon,
} from "react-share";
import { MdContentCopy } from "react-icons/md";

export interface ShareModalProps {
  open: boolean;
  onClose: (status: boolean) => void;
  url: string;
  title: string;
  programId: string;
  userId?: string;
  fullScreen?: boolean;
  playerWidth?: number;
}

const ShareModal = ({
  open = false,
  onClose,
  url,
  title,
  programId,
  userId = "anonymous",
  fullScreen = false,
  playerWidth = 270,
}: ShareModalProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [copySuccess, setCopySuccess] = useState("");
  const { t, i18n } = useTranslation();

  // 言語に応じたテキストを取得
  const getCopyLinkText = () => {
    return t('share.copyLink');
  };

  // URLにUTMパラメータを追加する関数
  const getShareUrl = (medium: string) => {
    const baseUrl = new URL(url);
    baseUrl.searchParams.append("utm_source", programId);
    baseUrl.searchParams.append("utm_medium", medium);
    baseUrl.searchParams.append("utm_campaign", userId);
    return baseUrl.toString();
  };

  // その他用のコピーテキスト
  const otherCopyText = getShareUrl("other");

  function closeModal() {
    setIsAnimating(false);
    setTimeout(() => {
      onClose(false);
    }, 300);
  }

  // コピー機能
  const copyToClipboard = async (text: string) => {
    try {
      // 現代的なブラウザでnavigator.clipboardが利用可能な場合
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopySuccess(t('share.copySuccess'));
        setTimeout(() => setCopySuccess(""), 2000);
      } else {
        // フォールバック：古いブラウザやHTTP環境用
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "absolute";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0, 99999);
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopySuccess(t('share.copySuccess'));
          setTimeout(() => setCopySuccess(""), 2000);
        } else {
          throw new Error('Copy command failed');
        }
      }
    } catch (err) {
      console.error('コピーに失敗しました:', err);
      setCopySuccess(t('share.copyFailed'));
      setTimeout(() => setCopySuccess(""), 2000);
    }
  };

  useEffect(() => {
    if (open) {
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`tw-absolute tw-inset-0 tw-z-50 tw-flex tw-items-end ${
        fullScreen ? "tw-justify-center" : ""
      }`}
      style={{
        backgroundColor: fullScreen ? "rgba(0, 0, 0, 0.5)" : "transparent",
        position: "absolute",
        width: fullScreen ? "100%" : `${playerWidth}px`,
        height: "100%",
        left: 0,
        top: 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div
        className={`tw-bg-white/70 tw-rounded-t-[20px] tw-transition-transform tw-duration-300 tw-ease-in-out tw-pointer-events-auto`}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          margin: "0 8px",
          width: `calc(100% - 16px)`,
          height: "250px",
          transform: isAnimating ? "translateY(0)" : "translateY(100%)",
        }}
      >
        <div className="tw-relative tw-h-full tw-py-2">
          <div className="tw-font-sans tw-h-full tw-overflow-auto">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-1 tw-border-b tw-border-gray-200">
              <div className="tw-font-medium tw-text-gray-800">
                {t('share.title')}
              </div>
              <button
                className="tw-text-gray-800 tw-bg-transparent tw-border-none tw-outline-none tw-shadow-none hover:tw-cursor-pointer"
                onClick={closeModal}
              >
                <IoIosClose size="1.5em" />
              </button>
            </div>
            
            <div className="tw-p-5">
              <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-mb-6">
                <div className="tw-flex tw-flex-col tw-items-center">
                  <FacebookShareButton
                    url={getShareUrl("facebook")}
                    hashtag="#foove"
                    className="tw-mb-1"
                  >
                    <FacebookIcon size={48} round />
                  </FacebookShareButton>
                  <span className="tw-text-xs tw-text-gray-700">Facebook</span>
                </div>
                
                <div className="tw-flex tw-flex-col tw-items-center">
                  <LineShareButton
                    url={getShareUrl("line")}
                    title={title}
                    className="tw-mb-1"
                  >
                    <LineIcon size={48} round />
                  </LineShareButton>
                  <span className="tw-text-xs tw-text-gray-700">LINE</span>
                </div>
                
                <div className="tw-flex tw-flex-col tw-items-center">
                  <EmailShareButton
                    url={getShareUrl("email")}
                    subject={title}
                    body=""
                    className="tw-mb-1"
                  >
                    <EmailIcon size={48} round />
                  </EmailShareButton>
                  <span className="tw-text-xs tw-text-gray-700">Email</span>
                </div>

                <div className="tw-flex tw-flex-col tw-items-center">
                  <button
                    onClick={() => copyToClipboard(otherCopyText)}
                    className="tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-200 tw-w-12 tw-h-12 tw-mb-1 tw-border-none tw-outline-none tw-cursor-pointer"
                  >
                    <MdContentCopy size="1.5rem" className="tw-text-gray-600" />
                  </button>
                  <span className="tw-text-xs tw-text-gray-700">
                    {copySuccess ? (
                    <p className="tw-text-xs tw-text-green-600 tw-mt-1 tw-text-center">{copySuccess}</p>
                    ):(
                      getCopyLinkText()
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal; 